/**
 * Error-classification helpers and telemetry session wrapper for the viewer-load flow.
 *
 * Keeping the pure classification logic in this module serves two purposes:
 *   1. The classifyFailureForTelemetry function can be unit-tested with zero mocking
 *      (no React, no Redux, no SDK, no OpenTelemetry).
 *   2. useHandleInit's catch block stays concise.
 *
 * Note: The ViewerLoadTelemetrySession class depends on the real `telemetry` module
 * and typically requires mocking in unit tests.
 */

import { telemetry, type TelemetryFailureDetails } from "telemetry";

import { ErrorKind } from "../sceneError/types";

// ─── InitCanvasError ──────────────────────────────────────────────────────────

/**
 * Wraps a WebGL / canvas-initialisation failure so the outer catch block can
 * distinguish it from SDK or network errors.
 */
export class InitCanvasError extends Error {
    constructor(cause: Error) {
        super("Failed to initialize 3D canvas");
        this.name = "InitCanvasError";
        this.cause = cause;
    }
}

// ─── HTTP status extraction ───────────────────────────────────────────────────

/** Extracts the HTTP status code from SDK error messages of the form `"HTTP Error:404 404"`. */
export function extractHttpStatus(message: string): number | undefined {
    const match = /^HTTP Error:(\d+)/.exec(message);
    return match ? Number.parseInt(match[1], 10) : undefined;
}

// ─── Classification ───────────────────────────────────────────────────────────

export type TelemetryFailureClassification = {
    /** Telemetry failure descriptor – passed directly to telemetry.endViewerLoad(). */
    failure: TelemetryFailureDetails;
    /**
     * Span attributes to stamp before closing the span.
     * Applied by the caller so this function stays side-effect free.
     */
    spanAttributes?: Record<string, string | boolean | number>;
    /** Stack / detail string for the Redux scene-status dispatch (may be undefined). */
    stack?: string;
};

/**
 * Best-effort detection of WebGL2 context loss on a canvas element.
 *
 * **Caveat:** `canvas.getContext(...)` may create a new WebGL2 context (and
 * allocate GPU resources) if one does not already exist.  This is acceptable
 * here because the function is only called from `classifyFailureForTelemetry`,
 * which runs after a failed canvas-init attempt — a context was already
 * created or attempted on this canvas.
 */
function isWebglContextLost(canvas: HTMLCanvasElement | null | undefined): boolean {
    if (!canvas) {
        return false;
    }

    const gl = canvas.getContext("webgl2") as WebGL2RenderingContext | null;
    return !!(gl && gl.isContextLost());
}

// ─── Telemetry session ────────────────────────────────────────────────────────

/**
 * Manages the OpenTelemetry span lifecycle for a single viewer-load attempt.
 *
 * Wraps the three-phase API (start → optional stamp → succeed/fail) so that
 * the telemetry wiring can be unit-tested by constructing this class directly
 * with only `telemetry` mocked — no hook render, no Redux, no SDK required.
 */
export class ViewerLoadTelemetrySession {
    public readonly span: ReturnType<typeof telemetry.startViewerLoad>;

    constructor(sceneId: string) {
        this.span = telemetry.startViewerLoad(sceneId);
    }

    /**
     * Stamps an arbitrary boolean attribute on the active span.
     * Safe to call even if telemetry is disabled (span is null).
     */
    stamp(key: string, value: boolean): void {
        this.span?.setAttribute(key, value);
    }

    /** Close the span as a success. */
    succeed(): void {
        telemetry.endViewerLoad(this.span, true);
    }

    /**
     * Apply any span attributes from the classification, then close the span
     * as a failure.  Attributes are always applied before the span is closed.
     */
    fail(classification: TelemetryFailureClassification): void {
        for (const [key, value] of Object.entries(classification.spanAttributes ?? {})) {
            this.span?.setAttribute(key, value);
        }
        telemetry.endViewerLoad(this.span, false, classification.failure);
    }
}

/**
 * Maps a caught error to a {@link TelemetryFailureClassification}.
 *
 * No React, Redux, or SDK dependencies — all runtime context is injected via `opts`.
 * Note: may call `canvas.getContext()` for WebGL context-loss detection, which
 * can allocate GPU resources (see {@link isWebglContextLost}).
 *
 * @param e    - The value caught by the outer try/catch.
 * @param opts - Runtime context: translation fn, online flag, and canvas ref.
 */
export function classifyFailureForTelemetry(
    e: unknown,
    opts: {
        t: (key: string) => string;
        isOnline: boolean;
        canvas?: HTMLCanvasElement | null;
    },
): TelemetryFailureClassification {
    const { t, isOnline, canvas } = opts;
    const webglContextLost = isWebglContextLost(canvas);

    // ── Branch 1: structured object error (RTK Query / API responses) ─────────
    if (e && typeof e === "object" && "error" in e) {
        const { error, statusCode } = e as { error: string; statusCode: number };

        if (
            statusCode === 401 ||
            statusCode === 403 ||
            error === "Not authorized" ||
            error === t("api.error.notAuthorized")
        ) {
            return {
                failure: {
                    errorKind: ErrorKind.NOT_AUTHORIZED,
                    statusCode: statusCode === 401 || statusCode === 403 ? statusCode : undefined,
                },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
            };
        }

        if (statusCode === 404 || error === "Scene not found" || error === t("api.error.sceneNotFound")) {
            return {
                failure: {
                    errorKind: ErrorKind.INVALID_SCENE,
                    statusCode: statusCode === 404 ? statusCode : undefined,
                },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
            };
        }

        if (statusCode === 410 || error === "Scene deleted" || error === t("api.error.sceneDeleted")) {
            return {
                failure: {
                    errorKind: ErrorKind.DELETED_SCENE,
                    statusCode: statusCode === 410 ? statusCode : undefined,
                },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
            };
        }

        return {
            failure: {
                errorKind: isOnline ? ErrorKind.UNKNOWN_ERROR : ErrorKind.OFFLINE_UNAVAILABLE,
                statusCode,
            },
            spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
        };
    }

    // ── Branch 2: Error instance (SDK throws, canvas init, generic JS errors) ─
    if (e instanceof Error) {
        const httpStatus = extractHttpStatus(e.message);

        if (httpStatus === 401 || httpStatus === 403) {
            return { failure: { errorKind: ErrorKind.NOT_AUTHORIZED, statusCode: httpStatus } };
        }

        if (httpStatus === 404) {
            return { failure: { errorKind: ErrorKind.EMPTY_PROJECT, statusCode: httpStatus } };
        }

        if (httpStatus === 410) {
            return { failure: { errorKind: ErrorKind.DELETED_SCENE, statusCode: httpStatus } };
        }

        if (e instanceof InitCanvasError) {
            return {
                failure: { errorKind: ErrorKind.INIT_CANVAS_ERROR },
                spanAttributes: webglContextLost ? { "webgl.context_lost": true } : undefined,
                stack: e.cause ? (e.cause as Error).stack : undefined,
            };
        }

        // Generic JS error
        const errorKind = isOnline ? ErrorKind.UNKNOWN_ERROR : ErrorKind.OFFLINE_UNAVAILABLE;
        const stack = e.stack ?? (typeof e.cause === "string" ? e.cause : `${e.name}: ${e.message}`);
        const sanitizedMessage = e.message.slice(0, 512);
        return {
            failure: { errorKind, message: sanitizedMessage },
            spanAttributes: { "error.message": sanitizedMessage },
            stack,
        };
    }

    // ── Branch 3: non-Error, non-object (string / number / plain object) ──────
    let stack: string | undefined;
    try {
        stack = JSON.stringify(e);
    } catch {
        stack = String(e);
    }
    return {
        failure: { errorKind: isOnline ? ErrorKind.UNKNOWN_ERROR : ErrorKind.OFFLINE_UNAVAILABLE },
        stack,
    };
}
